export interface Box {
  top: number
  left: number
  width: number
  height: number
}

type Side = 'top' | 'left'

interface PositionParams {
  element: HTMLElement
  container: HTMLElement
}

interface StyleParams {
  element: HTMLElement
  property: string
}

interface SideParams {
  element: HTMLElement
  side: Side
}

const EMPTY_BOX: Box = { top: 0, left: 0, width: 0, height: 0 }

const getStyle = ({ element, property }: StyleParams) =>
  element.style.getPropertyValue(property) ||
  window.getComputedStyle(element).getPropertyValue(property)

const getMargin = ({ element, side }: SideParams) =>
  parseInt(getStyle({ element, property: `margin-${side}` }), 10) || 0

const getInnerEdge = ({ element, side }: SideParams) => {
  const scroll = side === 'top' ? element.scrollTop : element.scrollLeft
  return parseInt(getStyle({ element, property: `border-${side}-width` }), 10) - scroll || 0
}

export const getOffset = (element: HTMLElement): Box => {
  const { documentElement, defaultView } = element.ownerDocument

  if (!documentElement.contains(element)) {
    return EMPTY_BOX
  }

  const { top, left, width, height } = element.getBoundingClientRect()

  if (!(width || height) || !defaultView) {
    return { top, left, width, height }
  }

  const pageTop = defaultView.scrollY || documentElement.scrollTop
  const pageLeft = defaultView.scrollX || documentElement.scrollLeft

  return {
    top: top + pageTop - (documentElement.clientTop || 0),
    left: left + pageLeft - (documentElement.clientLeft || 0),
    width,
    height,
  }
}

export const getPosition = ({ element, container }: PositionParams): Box => {
  const isFixed = getStyle({ element, property: 'position' }) === 'fixed'
  const { top, left, width, height } = isFixed
    ? element.getBoundingClientRect()
    : getOffset(element)

  if (isFixed) {
    return {
      top: top - getMargin({ element, side: 'top' }),
      left: left - getMargin({ element, side: 'left' }),
      width,
      height,
    }
  }

  const containerOffset =
    container === element.ownerDocument.documentElement ? EMPTY_BOX : getOffset(container)
  const originTop = containerOffset.top + getInnerEdge({ element: container, side: 'top' })
  const originLeft = containerOffset.left + getInnerEdge({ element: container, side: 'left' })

  return {
    top: top - originTop - getMargin({ element, side: 'top' }),
    left: left - originLeft - getMargin({ element, side: 'left' }),
    width,
    height,
  }
}
